import React, {useState} from 'react';
import { Button } from '@mui/material';
import * as ExcelJS from 'exceljs';
import formatDate from '../../formatDate';
import axios from 'axios';

export interface ColumnConfig {
  header: string;
  dataPath: string;
  width?: number;
  autoWidth?: boolean;
  isArray?: boolean;
  isFile?: boolean;
  isDate?: boolean;
  xCenter?: boolean;
  yCenter?: boolean;
  valueFunction?: (rowData: any) => {
    value: string | number;
    style?: {
      backgroundColor?: string;
      color?: string;
    };
  };
}

export interface ExcelDownloaderProps {
  data: any[];
  config: ColumnConfig[];
  fileName?: string;
  buttonLabel?: string;
  buttonStyle?: React.CSSProperties;
  documentHeading?: string;
  apiEndpoint?: string;
}

const ExcelDownloader: React.FC<ExcelDownloaderProps> = ({
  data: propsData,
  config,
  fileName = 'download',
  buttonLabel = 'Download Excel',
  buttonStyle,
  documentHeading = '',
  apiEndpoint
}) => {
  //const formatDate = (date: string) => {
  //  if (!date) return '';
  //  const d = new Date(date);
  //  return d.toLocaleDateString('en-GB', {
  //    day: '2-digit',
  //    month: '2-digit',
  //    year: 'numeric'
  //  });
  //};

  const getNestedValue = (obj: any, path: string) => {
    return path.split('?.').reduce((acc, part) => {
      if (acc === null || acc === undefined) return '';
      return acc[part.trim()];
    }, obj);
  };

  const calculateRowHeight = (content: string): number => {
    const lineCount = (content.match(/\n/g) || []).length + 1;
    return Math.max(lineCount * 15, 20);
  };

  const generateExcel = async () => {
    let excelData = propsData;

    if (apiEndpoint) {
      const response = await axios.get(`${apiEndpoint}`);
      excelData = response.data.results || response.data.result || response.data;
    }


    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.properties.defaultRowHeight = 20;

    // Set up columns
    worksheet.columns = config.map(col => ({
      header: col.header,
      width: col.width || (col.autoWidth ? undefined : 15)
    }));

    // Add main header row
    const mainHeaderRow = worksheet.getRow(1);
    mainHeaderRow.height = 30;
    
    // Merge cells for company name
    worksheet.mergeCells(1, 1, 1, config.length);
    const mainHeaderCell = mainHeaderRow.getCell(1);
    mainHeaderCell.value = 'SL FASTENERS PVT. LTD.';
    mainHeaderCell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    mainHeaderCell.font = {
      bold: true,
      size: 14,
      color: { argb: 'FFFFFF' }
    };
    mainHeaderCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '1F497D' }
    };

    // Add document heading
    const docHeadingRow = worksheet.getRow(2);
    docHeadingRow.height = 25;
    worksheet.mergeCells(2, 1, 2, config.length);
    const docHeadingCell = docHeadingRow.getCell(1);
    docHeadingCell.value = documentHeading;
    docHeadingCell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    docHeadingCell.font = {
      bold: true,
      size: 12,
      color: { argb: 'FFFFFF' }
    };
    docHeadingCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '4F81BD' }
    };

    // Style column headers
    const headerRow = worksheet.getRow(3);
    headerRow.height = 30;
    
    for (let i = 1; i <= config.length; i++) {
      const cell = headerRow.getCell(i);
      cell.value = config[i-1].header;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4F81BD' }
      };
      cell.font = { 
        color: { argb: 'FFFFFF' }, 
        bold: true 
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center'
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFFFFF' } },
        left: { style: 'thin', color: { argb: 'FFFFFF' } },
        bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
        right: { style: 'thin', color: { argb: 'FFFFFF' } }
      };
    }

    // Add data rows
    for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
      const item = excelData[rowIndex];
      const row = worksheet.getRow(rowIndex + 4);
      const rowColor = rowIndex % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
      
      for (let colIndex = 0; colIndex < config.length; colIndex++) {
        const col = config[colIndex];
        const cell = row.getCell(colIndex + 1);

        // Set default cell styling
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: rowColor }
        };

        cell.alignment = {
          horizontal: col.xCenter ? 'center' : 'left',
          vertical: col.yCenter ? 'middle' : 'top',
          wrapText: true
        };

        // Handle cell value and styling
        if (col.valueFunction) {
          const result = col.valueFunction(item);
          cell.value = result.value;
          
          //if (result.style) {
          //  if (result.style.backgroundColor) {
          //    //const bgColor = result.style.backgroundColor.replace('#', '');
          //    //cell.fill = {
          //    //  type: 'pattern',
          //    //  pattern: 'solid',
          //    //  fgColor: { argb: bgColor }
          //    //};
          //  }
          //  if (result.style.color) {
          //    const textColor = result.style.color.replace('#', '');
          //    cell.font = {
          //      color: { argb: textColor }
          //    };
          //  }
          //}
        } else {
          let value = getNestedValue(item, col.dataPath);

          if (col.isFile && value) {
            const files = Array.isArray(value) ? value : [value];
            if (files.length > 0) {
              const fileText = files
                .filter(file => file && file.name && file.url)
                .map(file => `${file.name}`)
                .join('\n');
              
              const firstValidFile = files.find(file => file && file.url);
              if (firstValidFile) {
                cell.value = {
                  text: fileText,
                  hyperlink: firstValidFile.url
                } as ExcelJS.CellHyperlinkValue;
          
                cell.font = {
                  color: { argb: '0000FF' },
                  underline: true
                };
              }
          
              const lines = fileText.split('\n').length;
              row.height = Math.max(row.height || 20, lines * 20);
            }
          } else if (col.isDate) {
            cell.value = formatDate(value);
          } else if (col.isArray && Array.isArray(value)) {
            cell.value = value.join('\n');
          } else {
            cell.value = value || '';
          }
        }

        // Adjust row height for text content
        if (cell.value && typeof cell.value === 'string') {
          const currentHeight = row.height || worksheet.properties.defaultRowHeight;
          const contentHeight = calculateRowHeight(cell.value);
          row.height = Math.max(currentHeight, contentHeight);
        }

        // Add cell borders
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
    }

    // Auto-fit columns
    config.forEach((col, index) => {
      if (col.autoWidth) {
        const column = worksheet.getColumn(index + 1);
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const length = cell.text ? String(cell.text).length : 0;
          maxLength = Math.max(maxLength, length);
        });
        column.width = maxLength + 2;
      }
    });

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      onClick={generateExcel}
      style={{
        background: '#76102a',
        marginRight: '1rem',
        ...buttonStyle
      }}
    >
      {buttonLabel}
    </Button>
  );
};

export default ExcelDownloader;